import { seatCallerId } from "@sameday/phone/caller-identity";
import type { PhoneSeat } from "../seats/types";

const seatAvailabilityRevisions = new Map<string, number>();

export type SeatAvailabilityOverlay = Readonly<{
  accountId: string;
  availability: "available" | "away";
  seatId: string;
  userId: string;
}>;

const seatAvailabilityOverlays = new Map<string, SeatAvailabilityOverlay>();

export const currentSeatPresenceRevision = (accountId: string): number =>
  seatAvailabilityRevisions.get(accountId) ?? 0;

/** Record that an authoritative event or write supersedes older snapshots. */
export const advanceSeatPresenceRevision = (accountId: string): number => {
  const revision = currentSeatPresenceRevision(accountId) + 1;
  seatAvailabilityRevisions.set(accountId, revision);
  return revision;
};

/** Capture the authority revision before starting an own-seat request. */
export const beginSeatAvailabilitySnapshot = (accountId: string): number =>
  currentSeatPresenceRevision(accountId);

/** Keep an exact-seat local intent visible while its serialized PATCH settles. */
export const setSeatAvailabilityOverlay = (
  overlay: SeatAvailabilityOverlay,
): void => {
  seatAvailabilityOverlays.set(overlay.accountId, overlay);
};

export const currentSeatAvailabilityOverlay = (
  accountId: string,
): SeatAvailabilityOverlay | null =>
  seatAvailabilityOverlays.get(accountId) ?? null;

/** Clear only the operation that still owns this account's local overlay. */
export const clearSeatAvailabilityOverlay = (
  target: Pick<SeatAvailabilityOverlay, "accountId" | "seatId" | "userId">,
): void => {
  const overlay = seatAvailabilityOverlays.get(target.accountId);
  if (overlay?.seatId === target.seatId && overlay.userId === target.userId) {
    seatAvailabilityOverlays.delete(target.accountId);
  }
};

export const clearSeatAvailabilityOverlays = (): void => {
  seatAvailabilityOverlays.clear();
};

export type SeatCallerOwner = Readonly<{ accountId: string; userId: string }>;
export type SeatCallerClaim = Readonly<{
  owner: SeatCallerOwner;
  revision: number;
}>;
export type SeatCallerReadClaim = Readonly<{
  owner: SeatCallerOwner;
  readSequence: number;
  negativeGeneration: number;
}>;
// The emergency fields (SD-20216) ride with the caller ID they describe, so a
// direct emergency call chooses its number from the same confirmed read. They
// are optional on the seat; a value built without them keeps today's path.
export type CallerSeatValue = Pick<
  PhoneSeat,
  | "id"
  | "accountId"
  | "userId"
  | "outboundIdentity"
  | "emergencyNumber"
  | "emergencyNumberId"
  | "emergencyStaticAddressId"
  | "emergencyStaticDirty"
  | "personalLines"
>;
export type SeatCallerSnapshot = Readonly<{
  owner: SeatCallerOwner;
  revision: number;
  seat: CallerSeatValue | null;
}>;
export type SeatCallerConfirmation = Readonly<{
  snapshot: SeatCallerSnapshot | null;
  status: "accepted" | "fenced" | "foreign" | "retired";
}>;
export type SeatCallerRefusal = Readonly<{
  snapshot: SeatCallerSnapshot | null;
  status: "accepted" | "fenced";
}>;

type SeatCallerState = {
  owner: SeatCallerOwner;
  revision: number;
  snapshot: SeatCallerSnapshot | null;
  requestedReads: number;
  confirmedRead: number;
  negativeGeneration: number;
};

type SeatCallerListener = (snapshot: SeatCallerSnapshot | null) => void;

const seatCallerStates = new Map<string, SeatCallerState>();
const seatCallerListeners = new Map<string, Set<SeatCallerListener>>();

const publishSeatCallerFact = (
  accountId: string,
  snapshot: SeatCallerSnapshot | null,
): void => {
  for (const listener of seatCallerListeners.get(accountId) ?? []) {
    listener(snapshot);
  }
};

/** Start an own-seat read without making an unconfirmed response authoritative. */
export const beginSeatCallerSnapshot = (
  accountId: string,
  userId: string,
): SeatCallerReadClaim => {
  let state = seatCallerStates.get(accountId);
  if (!state || state.owner.userId !== userId) {
    state = {
      owner: { accountId, userId },
      revision: 0,
      snapshot: null,
      requestedReads: 0,
      confirmedRead: 0,
      negativeGeneration: 0,
    };
    seatCallerStates.set(accountId, state);
    publishSeatCallerFact(accountId, null);
  }

  return {
    owner: state.owner,
    readSequence: ++state.requestedReads,
    negativeGeneration: state.negativeGeneration,
  };
};

export const currentSeatCallerSnapshot = (
  accountId: string,
): SeatCallerSnapshot | null =>
  seatCallerStates.get(accountId)?.snapshot ?? null;

/** Bind an outbound request to the caller fact that exists at HTTP dispatch. */
export const captureSeatCallerFact = (
  owner: SeatCallerOwner,
): SeatCallerClaim | null => {
  const state = seatCallerStates.get(owner.accountId);
  if (
    state?.owner !== owner ||
    state.snapshot === null ||
    seatCallerId(state.snapshot.seat) === null
  ) {
    return null;
  }
  return { owner, revision: state.revision };
};

/** Return a caller ID only when this snapshot still proves this exact seat. */
export const confirmedSeatCallerId = (
  snapshot: SeatCallerSnapshot | null,
  owner: SeatCallerOwner | null,
  seatId: string | null,
): string | null =>
  snapshot?.owner === owner && snapshot.seat?.id === seatId
    ? seatCallerId(snapshot.seat)
    : null;

const publishConfirmedCaller = (
  state: SeatCallerState,
  seat: CallerSeatValue | null,
): SeatCallerSnapshot => {
  state.revision += 1;
  if (seatCallerId(seat) === null) state.negativeGeneration += 1;
  state.snapshot = {
    owner: state.owner,
    revision: state.revision,
    seat: seat ? { ...seat, outboundIdentity: seatCallerId(seat) } : null,
  };
  publishSeatCallerFact(state.owner.accountId, state.snapshot);
  return state.snapshot;
};

/**
 * Confirm a read only when its owner is current and no later accepted fact
 * already superseded it. Failed or merely-started reads never reach here.
 */
export const confirmSeatCallerSnapshotOutcome = (
  claim: SeatCallerReadClaim,
  seat: CallerSeatValue | null,
): SeatCallerConfirmation => {
  const state = seatCallerStates.get(claim.owner.accountId);
  if (state?.owner !== claim.owner) {
    return { snapshot: null, status: "retired" };
  }
  if (
    claim.readSequence < state.confirmedRead ||
    claim.negativeGeneration !== state.negativeGeneration
  ) {
    return { snapshot: state.snapshot, status: "fenced" };
  }
  if (
    seat &&
    (seat.accountId !== claim.owner.accountId ||
      seat.userId !== claim.owner.userId)
  ) {
    return { snapshot: state.snapshot, status: "foreign" };
  }

  state.confirmedRead = claim.readSequence;
  return { snapshot: publishConfirmedCaller(state, seat), status: "accepted" };
};

/**
 * Apply a coded caller-ID refusal only when it belongs to the exact fact that
 * was current at transport dispatch. A newer confirmation, even of the same
 * number, fences the stale refusal.
 */
export const refuseSeatCallerIdentity = (
  claim: SeatCallerClaim,
): SeatCallerRefusal => {
  const state = seatCallerStates.get(claim.owner.accountId);
  if (
    state?.owner !== claim.owner ||
    state.snapshot === null ||
    state.snapshot.seat === null ||
    state.revision !== claim.revision
  ) {
    return { snapshot: state?.snapshot ?? null, status: "fenced" };
  }
  return {
    snapshot: publishConfirmedCaller(state, {
      ...state.snapshot.seat,
      outboundIdentity: null,
    }),
    status: "accepted",
  };
};

export const subscribeSeatCallerFacts = (
  accountId: string,
  listener: SeatCallerListener,
): (() => void) => {
  const listeners =
    seatCallerListeners.get(accountId) ?? new Set<SeatCallerListener>();
  seatCallerListeners.set(accountId, listeners);
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) seatCallerListeners.delete(accountId);
  };
};

/** Retire every caller fact and synchronously notify subscribers of the clear. */
export const invalidateSeatCallerFacts = (): void => {
  const accounts = [...seatCallerStates.keys()];
  seatCallerStates.clear();
  for (const accountId of accounts) publishSeatCallerFact(accountId, null);
};
